import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange } from '../../range-validate';
import { SwpCalculatorService, SwpResult, SWP_CONSTRAINTS } from './swp-calculator.service';

@Component({
  selector: 'app-swp-calculator',
  standalone: true,
  imports: [FormsModule],
  providers: [InvestmentChartService],
  templateUrl: './swp-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./swp-calculator.scss']
})
export class SwpCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(SwpCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('growthRef')    growthRef!:    ElementRef<HTMLCanvasElement>;

  readonly C = SWP_CONSTRAINTS;
  readonly Math = Math;

  initialInvestment = 2_000_000;
  monthlyWithdrawal = 15_000;
  annualRate = 8;
  tenureYears = 20;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     SwpResult | null = null;
  activeTab:  'yearly' | 'monthly' = 'yearly';

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.initialInvestment, this.C.initialInvestment.min, this.C.initialInvestment.max, 'Initial investment');
    if (e1) errors['initialInvestment'] = e1;
    const e2 = validateRange(this.monthlyWithdrawal, this.C.monthlyWithdrawal.min, this.C.monthlyWithdrawal.max, 'Monthly withdrawal');
    if (e2) errors['monthlyWithdrawal'] = e2;
    const e3 = validateRange(this.annualRate, this.C.rate.min, this.C.rate.max, 'Expected return');
    if (e3) errors['annualRate'] = e3;
    const e4 = validateRange(this.tenureYears, this.C.tenureYears.min, this.C.tenureYears.max, 'Withdrawal duration');
    if (e4) errors['tenureYears'] = e4;
    else if (!Number.isInteger(Number(this.tenureYears))) errors['tenureYears'] = 'Duration must be a whole number of years.';
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'tenureYears') this.tenureYears = Math.floor(this.tenureYears || 0);
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { initialInvestment: true, monthlyWithdrawal: true, annualRate: true, tenureYears: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(
      this.initialInvestment, this.monthlyWithdrawal, this.annualRate, Math.floor(this.tenureYears)
    );
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Total Withdrawn', value: r.totalWithdrawn, color: '#10b981' },
        { label: 'Remaining Balance', value: r.finalBalance, color: '#3b82f6' }
      ], 'Initial + Growth');
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.years.map(row => `Yr ${row.year}`),
        [{ label: 'Balance', data: r.years.map(row => Math.round(row.balance)), color: '#3b82f6' }]
      );
    }, 60);
  }

  reset(): void {
    this.initialInvestment = 2_000_000;
    this.monthlyWithdrawal = 15_000;
    this.annualRate        = 8;
    this.tenureYears       = 20;
    this.formErrors        = {};
    this.touched           = {};
    this.showFormErrors    = false;
    this.calculated         = false;
    this.result             = null;
    this.activeTab          = 'yearly';
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
